import { useMemo, useState } from "react";
import { Table, Badge, Button, Pagination, Form } from "react-bootstrap";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

const themeColors = {
  primary: "#3C6F7D",
  secondary: "#5F6D7E",
  success: "#0CA49D",
  warning: "#FFA319",
  error: "#FF1943",
  info: "#97CFC0",
  black: "#101828",
  white: "#ffffff",
  stroke: "#D0D5DD", // stroke
  primaryAlt: "#000C57",
};

function normalizeLogCourseNextButton(input) {
  // ✅ support: array langsung
  if (Array.isArray(input)) return input;

  // ✅ support: object wrapper { logCourseNextButton: [...] }
  if (input && Array.isArray(input.logCourseNextButton)) return input.logCourseNextButton;

  // ✅ support: string JSON
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.logCourseNextButton)) return parsed.logCourseNextButton;
    } catch (e) {
      return [];
    }
  }

  return [];
}

// ✅ Formatter tanggal Indonesia (manusiawi)
// Support format createdAt:
// 1) "9/1/2026, 14.05.19" (d/M/yyyy, HH.mm.ss)
// 2) "1/8/2026, 2:51:12 PM" (M/D/yyyy, h:mm:ss AM/PM)
function parseCreatedAtToDate(createdAt) {
  const s = String(createdAt || "").trim();
  if (!s) return null;

  // Format 1: d/M/yyyy, HH.mm.ss
  const m1 = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2})\.(\d{2})\.(\d{2})$/
  );
  if (m1) {
    const d = Number(m1[1]);
    const mo = Number(m1[2]);
    const y = Number(m1[3]);
    const hh = Number(m1[4]);
    const mm = Number(m1[5]);
    const ss = Number(m1[6]);
    const dt = new Date(y, mo - 1, d, hh, mm, ss);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // Format 2: M/D/yyyy, h:mm:ss AM/PM
  const m2 = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/i
  );
  if (m2) {
    const mo = Number(m2[1]);
    const d = Number(m2[2]);
    const y = Number(m2[3]);

    let hh = Number(m2[4]);
    const mm = Number(m2[5]);
    const ss = Number(m2[6]);
    const ap = String(m2[7]).toUpperCase();

    // convert ke 24 jam
    if (ap === "PM" && hh < 12) hh += 12;
    if (ap === "AM" && hh === 12) hh = 0;

    const dt = new Date(y, mo - 1, d, hh, mm, ss);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // Fallback: coba parse native (kalau environment support)
  const dtNative = new Date(s);
  return Number.isNaN(dtNative.getTime()) ? null : dtNative;
}

function formatHumanDateTime(date) {
  if (!date || Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function formatEpochMsToHuman(ms) {
  const n = Number(ms);
  if (!n) return "-";
  const dt = new Date(n);
  return Number.isNaN(dt.getTime()) ? String(ms) : formatHumanDateTime(dt);
}

function formatDeltaMs(deltaMs) {
  const abs = Math.abs(deltaMs);
  const totalSec = Math.floor(abs / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;

  const core = min > 0 ? `${min}m ${sec}s` : `${sec}s`;
  return deltaMs < 0 ? `Kurang ${core}` : `Lewat ${core}`;
}

function BoolBadge({ value }) {
  const isTrue = Boolean(value);
  return (
    <Badge
      bg="light"
      style={{
        border: `1px solid ${themeColors.stroke}`,
        color: isTrue ? themeColors.success : themeColors.secondary,
        fontWeight: 700,
      }}
    >
      {isTrue ? "true" : "false"}
    </Badge>
  );
}

export default function CourseNextButtonLogModal({
  logs, // bisa: array ATAU { logCourseNextButton: [...] } ATAU string JSON
  buttonLabel = "Lihat Log Aktivitas",
  modalTitle = "Log Aktivitas: clickNext",
}) {
  const [open, setOpen] = useState(false);

  // pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // raw detail modal
  const [openRaw, setOpenRaw] = useState(false);
  const [rawJson, setRawJson] = useState("");

  const safeLogs = useMemo(() => normalizeLogCourseNextButton(logs), [logs]);

  const rows = useMemo(() => {
    const mapped = safeLogs.map((item, idx) => {
      const minimumTimeEnd = Number(item?.data?.minimumTimeEnd || 0);
      const currentTime = Number(item?.data?.currentTime || 0);
      const deltaMs = currentTime - minimumTimeEnd;

      // event time untuk sorting: prefer epoch (currentTime sering sudah epoch), fallback idx
      const eventAtMs = currentTime || minimumTimeEnd || 0;

      // ✅ createdAt manusiawi (kalau gagal parse, fallback pakai epoch, kalau tetap gagal tampilkan string asli)
      const createdAtDate =
        parseCreatedAtToDate(item?.createdAt) || (eventAtMs ? new Date(eventAtMs) : null);
      const createdAtDisplay =
        createdAtDate && !Number.isNaN(createdAtDate.getTime())
          ? formatHumanDateTime(createdAtDate)
          : String(item?.createdAt || "-");

      // ✅ minimumTimeEnd & currentTime manusiawi (raw tetap ada di tooltip/title)
      const minimumTimeEndDisplay = formatEpochMsToHuman(minimumTimeEnd);
      const currentTimeDisplay = formatEpochMsToHuman(currentTime);

      const sortKey =
        createdAtDate && !Number.isNaN(createdAtDate.getTime())
          ? createdAtDate.getTime()
          : eventAtMs;

      // ✅ tambahan: showProgressUsed & selectedPlayer (kalau tidak ada => null, UI tampil "-")
      const selectedPlayerRaw = item?.data?.selectedPlayer;
      const selectedPlayerNum = Number(selectedPlayerRaw);
      const selectedPlayer = Number.isFinite(selectedPlayerNum) ? selectedPlayerNum : null;

      return {
        _id: `${item?.userId || "u"}-${idx}-${eventAtMs}`,
        createdAt: item?.createdAt,
        createdAtDisplay,
        sortKey,

        event: item?.event,
        userId: item?.userId,
        courseId: item?.data?.courseId,
        membershipId: item?.data?.membershipId,
        submateriId: item?.data?.submateriId,
        submateriTitle: item?.data?.submateriTitle,
        quiz: item?.data?.quiz,

        isDoneWatchingVideo: Boolean(item?.data?.isDoneWatchingVideo),
        isDone: Boolean(item?.data?.isDone),

        showProgressUsed: item?.data?.showProgressUsed,
        selectedPlayer,
        tipe: item?.data?.tipe,

        minimumTimeEnd,
        currentTime,
        minimumTimeEndDisplay,
        currentTimeDisplay,

        deltaMs,
        deltaDisplay: formatDeltaMs(deltaMs),

        raw: item,
        eventAtMs,
      };
    });

    // terbaru dulu
    mapped.sort((a, b) => (b.sortKey || 0) - (a.sortKey || 0));
    return mapped;
  }, [safeLogs]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pagedRows = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize, totalPages]);

  const handleOpen = () => {
    setOpen(true);
    // reset page biar enak saat buka ulang
    setPage(1);
  };

  const handleClose = () => setOpen(false);

  const handlePageSizeChange = (e) => {
    const next = Number(e.target.value);
    setPageSize(next);
    setPage(1);
  };

  const handleOpenRaw = (item) => {
    setRawJson(JSON.stringify(item, null, 2));
    setOpenRaw(true);
  };

  const handleCopyRaw = async () => {
    try {
      await navigator?.clipboard.writeText(rawJson);
    } catch (e) {
      // kalau clipboard tidak available, yaudah diam (dev biasanya paham)
    }
  };

  // bikin pagination item ringkas
  const pageItems = useMemo(() => {
    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
          {p}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages]);

  return (
    <>
      {/* Tombol untuk buka modal */}
      <Button
        onClick={handleOpen}
        style={{
          background: themeColors.primary,
          borderColor: themeColors.primary,
          fontWeight: 700,
        }}
      >
        {buttonLabel}
      </Button>

      {/* Modal utama */}
      <Modal isOpen={open} toggle={handleClose} size="lg">
        <ModalHeader toggle={handleClose}>{modalTitle}</ModalHeader>

        <ModalBody style={{ overflowX: "auto" }}>
          {/* Header tools */}
          {/* <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ color: themeColors.secondary, fontWeight: 700 }}>
              Total data: {total}
            </div>

            <Form.Select
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 140 }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </Form.Select>
          </div> */}

          {/* Table */}
          {/* ✅ Kolom dipersempit & digabung agar tidak perlu scroll kanan-kiri */}
          <Table hover size="md" style={{ marginBottom: 12, tableLayout: "fixed", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 170 }}>Waktu</th>
                <th style={{ width: 150 }}>ID</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 210 }}>Timing</th>
                <th>Raw</th>
              </tr>
            </thead>

            <tbody>
              {pagedRows?.map((r) => (
                <tr key={r?._id}>
                  {/* ✅ tampil manusiawi, raw tetap ada di title */}
                  <td style={{ whiteSpace: "nowrap" }} title={String(r?.createdAt || "")}>
                    {r?.createdAtDisplay}
                  </td>

                  {/* ✅ gabung ID jadi multiline biar muat */}
                  <td style={{ wordBreak: "break-word" }}>
                    <div style={{ fontWeight: 800, color: themeColors.black }}>
                      {r?.courseId}/{r?.membershipId}
                    </div>
                    <div style={{ color: themeColors.secondary }}>
                      Sub: {r?.submateriId}
                    </div>
                    <div style={{ color: themeColors.secondary }}>
                      Title: {r?.submateriTitle || "-"}
                    </div>
                  </td>

                  {/* ✅ gabung status jadi multiline */}
                  <td style={{ wordBreak: "break-word" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ color: themeColors.secondary, fontWeight: 700 }}>Video</span>
                      <BoolBadge value={r?.isDoneWatchingVideo} />
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                      <span style={{ color: themeColors.secondary, fontWeight: 700 }}>Done</span>
                      <BoolBadge value={r?.isDone} />
                    </div>

                    <div style={{ marginTop: 6, color: themeColors.secondary }}>
                      Quiz: <span style={{ fontWeight: 800, color: themeColors.black }}>{r?.quiz ?? "-"}</span>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                      <span style={{ color: themeColors.secondary, fontWeight: 700 }}>Progress</span>
                      {r?.showProgressUsed === null || r?.showProgressUsed === undefined ? (
                        <span style={{ color: themeColors.secondary, fontWeight: 700 }}>-</span>
                      ) : (
                        <BoolBadge value={r?.showProgressUsed} />
                      )}
                    </div>

                    <div style={{ marginTop: 6, color: themeColors.secondary }}>
                      Player:{" "}
                      <span style={{ fontWeight: 800, color: themeColors.black }}>
                        {r?.selectedPlayer === null || r?.selectedPlayer === undefined ? "-" : r?.selectedPlayer}
                      </span>
                    </div>

                    <div style={{ marginTop: 6, color: themeColors.secondary }}>
                      Tipe:{" "}
                      <span style={{ fontWeight: 800, color: themeColors.black }}>
                        {r?.tipe === null || r?.tipe === undefined ? "-" : r?.tipe}
                      </span>
                    </div>
                  </td>

                  {/* ✅ gabung timing jadi multiline, raw angka tetap ada di title */}
                  <td style={{ wordBreak: "break-word" }}>
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        fontWeight: 800,
                        color: r?.deltaMs < 0 ? themeColors.warning : themeColors.success,
                      }}
                    >
                      {r?.deltaDisplay}
                    </div>

                    <div style={{ marginTop: 6, color: themeColors.secondary, fontSize: 12 }}>
                      Min:
                      <span
                        style={{ marginLeft: 6, fontFamily: "monospace", color: themeColors.black, fontWeight: 700 }}
                        title={String(r?.minimumTimeEnd || "")}
                      >
                        {r?.minimumTimeEndDisplay}
                      </span>
                    </div>

                    <div style={{ color: themeColors.secondary, fontSize: 12 }}>
                      Now:
                      <span
                        style={{ marginLeft: 6, fontFamily: "monospace", color: themeColors.black, fontWeight: 700 }}
                        title={String(r?.currentTime || "")}
                      >
                        {r?.currentTimeDisplay}
                      </span>
                    </div>
                  </td>

                  <td>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleOpenRaw(r?.raw)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}

              {pagedRows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 18 }}>
                    Tidak ada data.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <Form.Select
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 140 }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </Form.Select>

            <div style={{ color: themeColors.secondary }}>
              Halaman <b>{Math.min(page, totalPages)}</b> dari <b>{totalPages}</b>
            </div>

            <Pagination style={{ margin: 0 }}>
              <Pagination.First onClick={() => setPage(1)} disabled={page <= 1} />
              <Pagination.Prev onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} />
              {pageItems}
              <Pagination.Next onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} />
              <Pagination.Last onClick={() => setPage(totalPages)} disabled={page >= totalPages} />
            </Pagination>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={handleClose}>
            Tutup
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal raw JSON */}
      <Modal isOpen={openRaw} toggle={() => setOpenRaw(false)} size="lg">
        <ModalHeader toggle={() => setOpenRaw(false)}>Raw JSON</ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleCopyRaw}
            >
              Copy
            </Button>
          </div>

          <pre
            style={{
              margin: 0,
              padding: 12,
              borderRadius: 10,
              border: `1px solid ${themeColors.stroke}`,
              background: "#f8fafc",
              maxHeight: 420,
              overflow: "auto", // ✅ raw boleh scroll
            }}
          >
            {rawJson}
          </pre>
        </ModalBody>
      </Modal>
    </>
  );
}